import { useEffect, useRef } from 'react';

type Dismissable = {
  id: number;
  close: () => void;
  getElement: () => HTMLElement | null;
};

const openDialogs: Dismissable[] = [];
let nextId = 1;
let listenerAttached = false;

function stackingScore(element: HTMLElement): number {
  let score = 0;
  let node: HTMLElement | null = element;
  while (node) {
    const z = Number.parseInt(window.getComputedStyle(node).zIndex, 10);
    if (!Number.isNaN(z)) score = Math.max(score, z);
    node = node.parentElement;
  }
  return score;
}

function topmostDialog(): Dismissable | null {
  const live = openDialogs.filter((entry) => entry.getElement()?.isConnected);
  if (live.length === 0) return null;

  live.sort((a, b) => {
    const aElement = a.getElement()!;
    const bElement = b.getElement()!;
    const byLayer = stackingScore(aElement) - stackingScore(bElement);
    if (byLayer !== 0) return byLayer;
    const position = aElement.compareDocumentPosition(bElement);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return a.id - b.id;
  });

  return live[live.length - 1];
}

function shouldLeaveEscapeAlone(event: KeyboardEvent): boolean {
  const active = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : event.target instanceof HTMLElement
      ? event.target
      : null;
  if (!active) return false;
  if (active.getAttribute('aria-expanded') === 'true') return true;
  return Boolean(active.closest('[aria-expanded="true"]'));
}

function onEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  if (openDialogs.length === 0 || event.defaultPrevented) return;
  if (shouldLeaveEscapeAlone(event)) return;

  const dialog = topmostDialog();
  if (!dialog) return;

  event.preventDefault();
  event.stopPropagation();
  dialog.close();
}

function ensureListener() {
  if (listenerAttached || typeof document === 'undefined') return;
  document.addEventListener('keydown', onEscape, true);
  listenerAttached = true;
}

function registerDismissable(entry: Omit<Dismissable, 'id'>): () => void {
  ensureListener();
  const registered: Dismissable = { ...entry, id: nextId++ };
  openDialogs.push(registered);
  return () => {
    const index = openDialogs.findIndex((item) => item.id === registered.id);
    if (index >= 0) openDialogs.splice(index, 1);
  };
}

/** Closes this dialog on Escape, using the same callback as its close button. Only the front dialog closes. */
export function useDismissOnEscape(isOpen: boolean, onClose: () => void) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    return registerDismissable({
      getElement: () => elementRef.current,
      close: () => onCloseRef.current(),
    });
  }, [isOpen]);

  return elementRef;
}
